import React from 'react';
import styled, { keyframes } from 'styled-components';

interface ButtonProps {
  text: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  outline?: boolean;
  bgColor?: string;
  textColor?: string;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  icon?: React.ReactNode;
  responsive?: boolean; // If true, shows icon on mobile instead of text
  showPlusIcon?: boolean; // Whether to show the plus icon alongside the text
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['responsive', 'isLoading', 'bgColor', 'textColor', 'outline'].includes(prop),
})<{ 
  outline?: boolean; 
  isLoading?: boolean;
  bgColor?: string;
  textColor?: string;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  responsive?: boolean;
}>`
  width: ${(props) => props.width || '100%'};
  min-width: ${(props) => props.minWidth || 'auto'};
  max-width: ${(props) => props.maxWidth || '100%'};
  padding: 14px;
  border: ${(props) => (props.outline ? `2px solid ${props.bgColor || '#000'}` : 'none')};
  border-radius: 30px;
  font-size: 16px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: ${(props) => (props.disabled || props.isLoading ? 'not-allowed' : 'pointer')};
  background-color: ${(props) => (props.outline ? 'transparent' : props.bgColor || '#000')};
  color: ${(props) => props.textColor || (props.outline ? props.bgColor || '#000' : '#fff')};
  opacity: ${(props) => (props.disabled ? '0.6' : '1')};
  transition: all 0.2s;
  position: relative;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: ${(props) => props.responsive ? '40px' : '100%'};
    height: ${(props) => props.responsive ? '40px' : 'auto'};
    min-width: ${(props) => props.responsive ? '40px' : props.minWidth || 'auto'};
    padding: ${(props) => props.responsive ? '0' : '14px'};
    border-radius: ${(props) => props.responsive ? '50%' : '30px'};
    aspect-ratio: ${(props) => props.responsive ? '1 / 1' : 'auto'};
  }
  
  &:hover {
    opacity: ${(props) => (props.disabled || props.isLoading ? '0.6' : '0.9')};
  }
`;

const TextContent = styled.span.withConfig({
  shouldForwardProp: (prop) => !['responsive', 'hasPlusIcon'].includes(prop),
})<{ responsive?: boolean; hasPlusIcon?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.hasPlusIcon ? '8px' : '0'};
  
  @media (max-width: 480px) {
    display: ${(props) => props.responsive ? 'none' : 'flex'};
  }
`;

const IconContent = styled.span.withConfig({
  shouldForwardProp: (prop) => !['responsive'].includes(prop),
})<{ responsive?: boolean }>`
  display: none; /* Hidden by default */
  font-size: 24px;
  
  @media (max-width: 480px) {
    display: ${(props) => props.responsive ? 'flex' : 'none'};
    align-items: center;
    justify-content: center;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: ${spin} 1s ease-in-out infinite;
  position: relative;
`;

const Button: React.FC<ButtonProps> = ({
  text,
  onClick,
  loading = false,
  disabled = false,
  outline = false,
  bgColor,
  textColor,
  width,
  minWidth,
  maxWidth,
  icon = '+',
  responsive = false,
  showPlusIcon = false,
}) => {
  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled || loading}
      outline={outline}
      isLoading={loading}
      bgColor={bgColor}
      textColor={textColor}
      width={width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      responsive={responsive}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          <TextContent responsive={responsive} hasPlusIcon={showPlusIcon}>
            {text}
            {showPlusIcon && !responsive && <span>+</span>}
          </TextContent>
          <IconContent responsive={responsive}>{icon}</IconContent>
        </>
      )}
    </StyledButton>
  );
};

export default Button;

// Last modified: 2025-04-11 18:36:34 by rinae-delfix